import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ShFieldContext, SH_FIELD } from './field.context';

let nextFieldId = 0;

/**
 * The form field: label, control, help and error, replacing the hand-written
 * `.field` markup. The host element IS the `.field` (or `.field-inline`), so
 * the central field styles in @seahorse/theme apply unchanged:
 *
 *   <sh-field label="user.name" help="user.nameHelp" [error]="error" required>
 *     <sh-input [(value)]="name" />
 *   </sh-field>
 *
 * How the control cooperates (the cleanest option: no wrapping markup, no
 * per-page wiring): `sh-field` provides `SH_FIELD` through DI and every
 * `sh-*` form control inside injects it, so
 * - the label's `for` and the control's native `id` share one generated id,
 * - `readonly` makes the control render itself read-only with the lock icon,
 * - a non-empty `error` gives the control its invalid look.
 * A control outside an `sh-field` works the same, without those three.
 *
 * Texts (`label`, `help`, `error`) are translation keys or plain text.
 * Default change detection: the projected control's state (and a page's
 * `error` getter) must be able to change without an input changing.
 */
@Component({
  selector: 'sh-field',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, TranslateModule],
  providers: [
    { provide: SH_FIELD, useExisting: forwardRef(() => ShFieldComponent) },
  ],
  host: {
    class: 'field',
    '[class.field-inline]': 'inline()',
    '[class.field-readonly]': 'readonly()',
  },
  template: `
    <label *ngIf="label()" [attr.for]="controlId">
      {{ label() | translate }}
      <span class="required" *ngIf="required()" aria-hidden="true">*</span>
    </label>
    <ng-content></ng-content>
    <div class="help" *ngIf="help()">{{ help() | translate }}</div>
    <div class="error" *ngIf="error()" role="alert">
      {{ error() | translate }}
    </div>
  `,
})
export class ShFieldComponent implements ShFieldContext {
  readonly label = input<string>();
  readonly help = input<string>();
  readonly error = input<string | null>();
  readonly required = input(false, { transform: booleanAttribute });
  /** Label beside the control instead of above it. */
  readonly inline = input(false, { transform: booleanAttribute });
  /** The control renders read-only, with the lock icon. */
  readonly readonly = input(false, { transform: booleanAttribute });

  readonly controlId = `sh-field-${nextFieldId++}`;
}
